import { ZoomIn } from "lucide-react";
import { useEffect, useState } from "react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface ImageModalProps {
  src: string;
  /** Accessible name for the image (also the modal trigger's label). */
  alt?: string;
  /** Extra classes for the inline thumbnail image (e.g. to cap its height). */
  thumbnailClassName?: string;
}

export const ImageModal = ({
  src,
  alt = "Preview",
  thumbnailClassName,
}: ImageModalProps) => {
  const [open, setOpen] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset to the skeleton whenever the source changes (e.g. switching between
  // the redacted and original receipt) so the previous image is hidden
  // immediately and the browser loads the new one behind a placeholder. The
  // <img> keeps its box while hidden, so the layout size does not change.
  useEffect(() => {
    setImageLoaded(false);
  }, [src]);

  const handleOpen = () => setOpen(true);

  return (
    <>
      {/* The whole thumbnail is the trigger, so it is keyboard- and SR-accessible. */}
      <button
        type="button"
        data-test-id="zoom-button"
        onClick={handleOpen}
        className="group relative block w-full cursor-pointer border-0 bg-transparent p-0"
      >
        <img
          data-test-id="image-preview"
          src={src}
          alt={alt}
          className={cn(
            "object-contain w-full",
            thumbnailClassName,
            !imageLoaded && "invisible"
          )}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <Skeleton className="absolute inset-0 size-full rounded-md" />
        )}
        <div className="opacity-0 absolute top-0 left-0 w-full h-full bg-black/40 flex items-center justify-center transition-opacity group-hover:opacity-100">
          <ZoomIn className="w-8 h-8 text-white" />
        </div>
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="max-w-2xl overflow-hidden p-0 bg-card border-none shadow-xs"
          closeClassName="left-4 right-auto text-foreground"
        >
          <DialogTitle className="sr-only">{alt}</DialogTitle>
          <DialogDescription className="sr-only">{alt}</DialogDescription>
          <div className="relative">
            <img
              data-test-id="original-image"
              src={src}
              alt={alt}
              className="max-h-[90dvh] w-full object-contain"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
